/**
 * RMM display helpers — Datto status enums → labels + badge kinds. Pure
 * utilities, the RMM counterpart of `cobalt-helpers.ts`.
 */

import type { BadgeKind } from '@/lib/cobalt-helpers';

interface StatusMeta { label: string; kind: BadgeKind }

const PATCH_STATUS: Record<string, StatusMeta> = {
  FullyPatched:    { label: 'Fully patched',    kind: 'success' },
  ApprovedPending: { label: 'Approved pending', kind: 'warning' },
  RebootRequired:  { label: 'Reboot required',  kind: 'warning' },
  InstallError:    { label: 'Install error',    kind: 'destructive' },
  NoPolicy:        { label: 'No policy',        kind: 'neutral' },
  NoData:          { label: 'No data',          kind: 'neutral' },
};

const AV_STATUS: Record<string, StatusMeta> = {
  RunningAndUpToDate:    { label: 'Up to date',   kind: 'success' },
  RunningAndNotUpToDate: { label: 'Out of date',  kind: 'warning' },
  NotRunning:            { label: 'Not running',  kind: 'destructive' },
  NotDetected:           { label: 'Not detected', kind: 'destructive' },
};

const ALERT_PRIORITY_KIND: Record<string, BadgeKind> = {
  Critical:    'destructive',
  High:        'destructive',
  Moderate:    'warning',
  Low:         'info',
  Information: 'neutral',
};

/** Splits "ApprovedPending" → "Approved pending" for values we haven't mapped yet. */
function humanise(s: string): string {
  const spaced = s.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function patchStatusMeta(s: string | null): StatusMeta | null {
  if (!s) return null;
  return PATCH_STATUS[s] ?? { label: humanise(s), kind: 'neutral' };
}

export function avStatusMeta(s: string | null): StatusMeta | null {
  if (!s) return null;
  return AV_STATUS[s] ?? { label: humanise(s), kind: 'neutral' };
}

export function alertPriorityKind(p: string | null): BadgeKind {
  return (p && ALERT_PRIORITY_KIND[p]) || 'neutral';
}

export const PATCH_STATUS_OPTIONS = Object.entries(PATCH_STATUS).map(([value, m]) => ({ value, label: m.label }));
export const AV_STATUS_OPTIONS = Object.entries(AV_STATUS).map(([value, m]) => ({ value, label: m.label }));

/** Whole days since an ISO timestamp, or null. */
export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 86_400_000) : null;
}

/** "3m", "5h", "12d" — compact age for dense grid cells. */
export function fmtAge(iso: string | null): string {
  if (!iso) return '—';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(mins)) return '—';
  if (mins < 60) return `${Math.max(mins, 0)}m`;
  if (mins < 48 * 60) return `${Math.floor(mins / 60)}h`;
  return `${Math.floor(mins / 1440)}d`;
}

export function fmtBytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** Client column label: the linked Autotask company, else the raw Datto site name. */
export function deviceClientName(d: { autotaskCompanyName: string | null; siteName: string }): string {
  return d.autotaskCompanyName ?? d.siteName;
}
